import React, { useEffect, useMemo, useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  ArrowRight,
  Check,
  Send,
  Clock,
  Sparkles,
  AlertCircle
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";
import { contactAPI } from "../utils/api";

const contactMethods = [
  {
    icon: Mail,
    title: "Email Us",
    description: "Our team is here to help with any questions or issues.",
    value: "support@pms.example.com",
    link: "mailto:support@pms.example.io",
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  },
  {
    icon: MessageSquare,
    title: "Live Chat",
    description: "Connect with us instantly through our in-app chat.",
    value: "Available 24/7",
    link: "#",
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  },
  {
    icon: Phone,
    title: "Call Us",
    description: "Speak directly with our product specialists.",
    value: "+1 (555) 123-4567",
    link: "tel:+15551234567",
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  }
];

const offices = [
  {
    city: "San Francisco",
    address: "123 Innovation Way, Suite 400",
    country: "United States",
    timezone: "PST (UTC-8)"
  },
  {
    city: "London",
    address: "45 Canary Wharf, Level 12",
    country: "United Kingdom",
    timezone: "GMT (UTC+0)"
  },
  {
    city: "Singapore",
    address: "88 Marina Boulevard, Tower 2",
    country: "Singapore",
    timezone: "SGT (UTC+8)"
  }
];

const ContactUsPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "Sales Inquiry",
    message: ""
  });

  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right, .landing-page .section-animate"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await contactAPI.submitForm(formData);
      setLoading(false);
      setSubmitted(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        subject: "Sales Inquiry",
        message: ""
      });
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || err.response?.data?.errors?.[0] || "Something went wrong. Please try again later.";
      setError(errorMessage);
    }
  };

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} ctaTarget="contact-form" />

      <main>
        <section className="curved-section shell-sky section-animate pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="section-eyebrow reveal">
              <Sparkles className="w-4 h-4 inline mr-2" />
              Get in Touch
            </span>
            <h1 className="reveal stagger-1 mt-6 mb-8 text-gray-900">
              We're here to <br />
              <span className="text-blue-600">help you grow</span>
            </h1>
            <p className="text-lead reveal stagger-2 max-w-2xl mx-auto">
              Have questions about features, pricing, or how PMS can transform your team? 
              Reach out and we'll get back to you in record time.
            </p>
          </div>
        </section>

        <section className="shell-white section-animate py-24 -mt-20">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-start">
              <div className="reveal-left">
                <div className="space-y-6 mb-12">
                  {contactMethods.map((method, idx) => (
                    <a
                      key={idx}
                      href={method.link}
                      className="group card-marketing flex items-start gap-6 hover:shadow-2xl"
                    >
                      <div className={`w-14 h-14 ${method.color} rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110`}>
                        <method.icon className={`w-7 h-7 ${method.iconColor}`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-black text-gray-900 mb-1">{method.title}</h3>
                        <p className="text-gray-500 mb-2">{method.description}</p>
                        <span className="text-blue-600 font-bold group-hover:underline">{method.value}</span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="card-marketing bg-navy text-white relative overflow-hidden">
                   <div className="relative z-10">
                    <h3 className="text-3xl font-black mb-8 text-sky">Global Presence</h3>
                    <div className="space-y-10">
                      {offices.map((office, idx) => (
                        <div key={idx} className="flex items-start gap-6">
                          <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-6 h-6 text-sky" />
                          </div>
                          <div>
                            <div className="text-xl font-bold">{office.city}</div>
                            <div className="text-blue-100 opacity-80 mt-1">{office.address}</div>
                            <div className="text-blue-100 opacity-80">{office.country}</div>
                            <div className="flex items-center gap-2 mt-3 text-xs font-bold text-sky uppercase tracking-widest">
                              <Clock className="w-4 h-4" />
                              {office.timezone}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div id="contact-form" className="reveal-right">
                <div className="card-marketing p-10 lg:p-12 shadow-2xl relative bg-white !rounded-[40px]">
                  {submitted ? (
                    <div className="text-center py-12 animate-scale-in">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Check className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-3xl font-black text-gray-900 mb-4">Message Sent!</h3>
                      <p className="text-gray-600 text-lg mb-8">
                        Thank you for reaching out. A member of our team will contact you within the next 24 hours.
                      </p>
                      <button
                        onClick={() => setSubmitted(false)}
                        className="btn-primary w-full"
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-3xl font-black text-gray-900 mb-8">Send us a message</h3>
                      
                      {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-lg flex items-center gap-3 animate-shake">
                          <AlertCircle className="w-5 h-5 flex-shrink-0" />
                          <p className="text-sm font-bold">{error}</p>
                        </div>
                      )}

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-widest">First Name</label>
                            <input
                              type="text"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              required
                              className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all"
                              placeholder="John"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-widest">Last Name</label>
                            <input
                              type="text"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              required
                              className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all"
                              placeholder="Doe"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-widest">Work Email</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all"
                            placeholder="john@company.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-widest">Subject</label>
                          <select 
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all appearance-none cursor-pointer"
                          >
                            <option>Sales Inquiry</option>
                            <option>Technical Support</option>
                            <option>Billing Question</option>
                            <option>Partnership Opportunity</option>
                            <option>Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-widest">Message</label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            required
                            rows="5"
                            className="w-full px-6 py-4 rounded-lg bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-navy transition-all resize-none"
                            placeholder="How can we help you?"
                          ></textarea>
                        </div>
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn-primary w-full py-5 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <>
                              Send Message
                              <Send className="w-5 h-5" />
                            </>
                          )}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="shell-white text-center">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="mb-16">Join the teams building with PMS</h2>
            <div className="flex flex-wrap justify-center items-center gap-12 md:gap-24 opacity-40 grayscale reveal">
              {["TechCorp", "StartupX", "DesignLab", "CloudBase", "AgencyPro"].map((logo) => (
                <div key={logo} className="flex items-center gap-2 text-2xl font-black text-gray-800">
                  <Sparkles className="w-8 h-8" />
                  {logo}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="cta" className="shell-sky text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-navy">Prefer a live demo?</h2>
            <p className="text-lead mb-12">
              Schedule a 15-minute walkthrough with our product team to see exactly how 
              PMS can solve your specific challenges.
            </p>
            <button className="btn-secondary !bg-white">
              Schedule Demo Call
            </button>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default ContactUsPage;
